import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { Review } from "../../../api/content/reviewDecoders";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import EmptyState from "../../../ui/feedback/EmptyState";
import { useMemberNames } from "../../threads/data/useMemberNames";
import ConfirmedVersions from "../../versions/merge/ConfirmedVersions";
import { changedItemName, mergeApprovalText } from "./decisionLabels";
import HistoricalDecisions from "./HistoricalDecisions";

/** The decisions taken on the change set, newest first; those on an earlier head are greyed. */
export default function DecisionHistory({ review }: { review: Review }) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const people = useMemberNames(review.changeSet.projectId);
  return (
    <section className="app-card space-y-2 p-3" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-base font-semibold">{t("reviews.decisions.title")}</h3>
      {review.decisions.length === 0 ? <EmptyState>{t("reviews.decisions.none")}</EmptyState> : null}
      <ul className="space-y-1 text-sm">
        {review.decisions.map((decision) => (
          <li key={decision.decisionId} className={decision.outdated ? "app-text-muted" : ""}>
            <span className="font-semibold">{t(`reviews.decisions.kinds.${decision.kind}`)}</span>{" "}
            {decision.reviewer === people.currentUserId
              ? t("reviews.decisions.byYou", { time: formatTimeAgo(decision.at, i18n.resolvedLanguage) })
              : t("reviews.decisions.by", { name: people.nameOf(decision.reviewer), time: formatTimeAgo(decision.at, i18n.resolvedLanguage) })}
            {decision.outdated ? `, ${t("reviews.decisions.outdated")}` : ""}
            {decision.mergeApprovers === null ? "" : `, ${mergeApprovalText(decision.mergeApprovers, people.nameOf, t, i18n.resolvedLanguage)}`}
            {decision.comment !== "" ? <span>: {decision.comment}</span> : null}
            {decision.acknowledgementNote !== null ? (
              <span className="app-text-muted"> ({t("reviews.decisions.acknowledged", { note: decision.acknowledgementNote })})</span>
            ) : null}
            <ConfirmedVersions decision={decision} nameOf={(itemId) => changedItemName(review, itemId)} />
          </li>
        ))}
      </ul>
      {review.historicalReview ? <HistoricalDecisions history={review.historicalReview} /> : null}
    </section>
  );
}
